import { z } from 'zod';
import { OBJECTIVE_LEVELS, OBJECTIVE_STATUSES } from './objectives';
import { OBJECTIVE_PERFORMANCE_SOURCES } from './performance';
import { TRAFFIC_LIGHTS } from './status';

const ref = z.object({ id: z.string().uuid(), name: z.string() });
const light = z.enum(TRAFFIC_LIGHTS);

/** Período das metas operacionais mostradas na árvore (padrão: o mês atual dentro do horizonte). */
export const cascadeQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
});

/** Meta operacional: KPI do objetivo com a meta vigente do período (do mês; sem ela, a anual). */
export const cascadeKpiSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  unitLabel: z.string(),
  target: z.object({ periodType: z.enum(['MONTH', 'YEAR']), value: z.number() }).nullable(),
  /** Realizado x meta do mês e a performance calculada (T-061/T-064); null sem cálculo ou sem permissão. */
  month: z
    .object({
      actual: z.number().nullable(),
      target: z.number().nullable(),
      performance: z.number().nullable(),
      light,
    })
    .nullable(),
});
export type CascadeKpi = z.infer<typeof cascadeKpiSchema>;

/** Nó da árvore: objetivo ativo com o pai visível (null = raiz para quem vê), horizontais e KPIs. */
export const cascadeNodeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  level: z.enum(OBJECTIVE_LEVELS),
  status: z.enum(OBJECTIVE_STATUSES),
  perspective: ref,
  department: ref.nullable(),
  owner: ref.nullable(),
  parentId: z.string().uuid().nullable(),
  /** Há pai, mas de área fora do escopo de quem vê: o nó aparece como raiz. */
  parentHidden: z.boolean(),
  horizontal: z.array(ref),
  kpis: z.array(cascadeKpiSchema),
  /** Performance do objetivo no mês e no acumulado do ano (agregados da T-064b); null sem cálculo ou sem permissão. */
  performance: z
    .object({
      performance: z.number().nullable(),
      light,
      ytdPerformance: z.number().nullable(),
      ytdLight: light,
      /** KPIs próprios ou consolidada dos que o desdobram (DEC-032). */
      source: z.enum(OBJECTIVE_PERFORMANCE_SOURCES).nullable(),
    })
    .nullable(),
});
export type CascadeNode = z.infer<typeof cascadeNodeSchema>;

export const cascadeSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  /** Anos do horizonte do projeto (seletor de período). */
  years: z.array(z.number().int()),
  /** Quem não vê Indicadores não recebe KPIs; quem não vê Metas recebe KPIs sem a meta. */
  showKpis: z.boolean(),
  showTargets: z.boolean(),
  /** Quem vê Metas e Resultados recebe a performance e o semáforo (os mesmos números do scorecard). */
  showPerformance: z.boolean(),
  /** Quando os agregados do ano foram calculados (null = ainda não há cálculo). */
  computedAt: z.string().nullable(),
  nodes: z.array(cascadeNodeSchema),
});
export type Cascade = z.infer<typeof cascadeSchema>;

/** Nó com os filhos, para desenhar a árvore. */
export type CascadeTreeNode = CascadeNode & { children: CascadeTreeNode[]; context: boolean };

/**
 * Monta a árvore a partir dos nós. Com filtro (`keep`), ficam os nós que passam, os ancestrais deles e os
 * desdobramentos deles (os que não passam no filtro vêm marcados como contexto: a cadeia continua legível
 * e nenhum objetivo encontrado parece "sem desdobramento"). A ordem dos nós é preservada.
 */
export function buildCascadeTree(
  nodes: CascadeNode[],
  keep: (n: CascadeNode) => boolean = () => true,
): CascadeTreeNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const childrenOf = new Map<string, CascadeNode[]>();
  for (const n of nodes) {
    if (n.parentId) childrenOf.set(n.parentId, [...(childrenOf.get(n.parentId) ?? []), n]);
  }
  const included = new Map<string, boolean>(); // id → é só contexto?
  const matched = nodes.filter((n) => keep(n));
  for (const n of matched) included.set(n.id, false);
  for (const n of matched) {
    let up = n.parentId ? byId.get(n.parentId) : undefined;
    for (let guard = 0; up && guard < 10; guard += 1) {
      if (!included.has(up.id)) included.set(up.id, true);
      up = up.parentId ? byId.get(up.parentId) : undefined;
    }
    const down = [...(childrenOf.get(n.id) ?? [])];
    for (let guard = 0; down.length && guard < 10_000; guard += 1) {
      const child = down.shift()!;
      if (!included.has(child.id)) included.set(child.id, true);
      down.push(...(childrenOf.get(child.id) ?? []));
    }
  }
  const tree = new Map<string, CascadeTreeNode>();
  for (const n of nodes) {
    if (included.has(n.id)) tree.set(n.id, { ...n, children: [], context: included.get(n.id)! });
  }
  const roots: CascadeTreeNode[] = [];
  for (const node of tree.values()) {
    const parent = node.parentId ? tree.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}
